import EventType from 'ol/events/EventType';
import { AbstractLayerControl, AbstractLayerEntry } from './abstract_layer_control';

import { CLASS_CONTROL, CLASS_UNSELECTABLE } from 'ol/css';
import { listen } from 'ol/events';
import { unByKey } from 'ol/Observable';

import BaseEvent from 'ol/events/Event';

import { NAME_KEY, LEGEND_VISIBLE_KEY, ICON_KEY, FILTER_ACTIVE_KEY, FEATURE_INFO_ACTIVE_KEY, LAYER_ERROR_KEY } from '../layer';

import { faTriangleExclamation } from '@fortawesome/pro-solid-svg-icons';
import { faList, faPlay, faXmark, faGripDotsVertical, faImage, faInfoCircle, faFilter } from '@fortawesome/sharp-solid-svg-icons';
import { faX } from '@fortawesome/pro-light-svg-icons';

import { icon } from '@fortawesome/fontawesome-svg-core';

import { createElement } from '../../util/element';
import { sortable } from '../../interaction/sortable';

import { REMOVEABLE, FEATURE_INFO, OPACITY, FILTER, LEGEND, SORTABLE, TREE, createEmpty } from '../layer_capabilities';

import './layers.css';

const hiddenColumnStyles = {
    [REMOVEABLE]: 'hide-removeable',
    [FEATURE_INFO]: 'hide-feature-info',
    [OPACITY]: 'hide-opacity',
    [FILTER]: 'hide-filter',
    [LEGEND]: 'hide-legend',
    [SORTABLE]: 'hide-sortable',
    [TREE]: 'hide-tree'
}

const SORT_LAYER_EVENT = "SortLayerEvent";
const SELECT_LAYER_EVENT = "SelectLayerEvent";
const REMOVE_LAYER_EVENT = "RemoveLayerEvent";
const FILTER_LAYER_EVENT = "FilterLayerEvent";

const CLOSE_EVENT = "CloseEvent";

const sortableLayers = (element, options) => {
    const layerOptions = {
        dragHandle: "layer-sort-grip",
        itemClassName: "layer-entry",
        idleClassName: "layer-idle",
        dragX: false,
        draggingClassName: "layer-dragging"
    }
    const nextOptions = {...layerOptions, ...options};
    
    sortable(element, nextOptions)
}

const createInputElement = (type, className, parent, displayInput) => {
	const column = createElement("div", className, parent);

    if (displayInput === undefined || displayInput) {
        const element = createElement("input", undefined, column);
        element.type = type;
        return element;
    }
}

const createIcon = (parentElement, styleName, iconName, displayIcon) => {
   const iconElement = createElement("div", styleName, parentElement);
   
   if( displayIcon === undefined || displayIcon ) {
        iconElement.appendChild(icon(iconName).node[0]);
    }

   return iconElement;
}

const getLayerIcon = (layer) => {
    let layerIcon = null;
    
    if( layer.layer.get(LAYER_ERROR_KEY) ) {
        layerIcon = icon(faTriangleExclamation).node[0].outerHTML;
    } else {
        layerIcon = layer.getIcon();
    }
    
    return layerIcon;
}

class LayerTreeState {
    
    constructor(layer, parentElement) {
        this.layer = layer;
        this.parentElement = parentElement;
        
        this.element = createIcon(parentElement, "layer-tree-state", faPlay, layer.getLayers().length);
        this.element.addEventListener(EventType.CLICK, e => this.expand(), false);
        
        this.updateStyles();
    }
    
    expand() {
        const expanded = this.layer.isExpanded();
        this.layer.setExpanded( !expanded );
        
        this.updateStyles();
    }    
    
    updateStyles() {
        const expanded = this.layer.isExpanded();
        this.element.classList.toggle("is-expanded", expanded);
        
        if( expanded && this.control === undefined ) {
            this.initControl();
        } else if( !expanded && this.control ) {
            this.control.remove();
            this.control = undefined;
        }
    }
    
    initControl() {
        const target = this.parentElement.closest(".layer-entry");
        const control = new LayerListControl({
            container: target
        });
                
        const layer = this.layer;
        layer.getLayers().forEach(l => {
            const options = {};
            
            if( layer.isExclusive() ) {
                options.radioButtonGroup = `layer-group-${layer.getId()}`;
                options.radioButtonValue = l.getId();
            }
            
            const entry = control.addLayer(l, options);
            entry.updateAll();
            //layersElement.appendChild(entry.container);
            
            entry.visible.addEventListener(EventType.CHANGE, this.handleRadioButtonChange.bind(this), false); 
        }); 
        
        this.control = control;
        this.control.updateColumns();
    }
    
    handleRadioButtonChange(event) {
        const layer = this.layer;
        layer.getLayers().forEach(l => {
            l.setVisible(l.getId() === event.target.value);
        });
    }
    
}

class LayerIcon {
	constructor(parentElement) {
		this.icon = createElement("div", "layer-icon", parentElement);
	}
		
	set value(icon) {
		 if( icon ) {
			this.icon.replaceChildren();
			this.icon.innerHTML = icon;
		} else if( icon === undefined  ) {
			this.icon.replaceChildren();
			this.icon.appendChild(icon( faImage ).node[0]);
		}
	}
}

class LayerLabel {
	constructor(parentElement, callback) {
		this.element = createElement("div", "layer-name", parentElement);
		this.callback = callback;
    }

    update(clickable, label) {
		if( clickable && this.anchor === undefined ) {
            this.element.replaceChildren();
            
            this.span = undefined;
            
            this.anchor = createElement("a", undefined, this.element);
            this.anchor.href = "#";
            
            this.anchor.addEventListener("click", this.handleClick.bind(this), false);
		} else if( !clickable && this.anchor ) {
            this.element.replaceChildren();            
            this.anchor = undefined;
        }
        
        if( !clickable && this.span === undefined ) {
            this.span = createElement("span", undefined, this.element);
        }
        
        if( this.anchor ) {
            this.anchor.innerText = label;
            this.anchor.title = label;
        } else if( this.span ) {
            this.span.innerText = label;
            this.span.title = label;
        }
	}
	
    handleClick(event) {
        console.log(event);
        event.preventDefault();
        this.callback();
    }
    
}

class IconButton {
    constructor(parentElement, icon, styleName, displayIcon) {
        this.icon = createIcon(parentElement, `${styleName} icon-button`, icon, displayIcon);
        this.value = false;
    }
    
    get checked() {
        return this.value;
    }
    
    set checked(value) {
        this.icon.classList.toggle("is-checked", !!value);
        this.value = value;
    }
    
    set disabled(value) {
        this.icon.classList.toggle("is-disabled", !!value);
    }
    
    addEventListener(eventType, listener, b) {
        return this.icon.addEventListener(eventType, listener, b);
    }
}

const createRemoveIcon = (parentElement, displayIcon) => createIcon(parentElement, "layer-remove", faXmark, displayIcon);
const createLayerSortGrip = (parentElement, displayIcon) => createIcon(parentElement, "layer-sort-grip", faGripDotsVertical, displayIcon);

const createLayerVisible = (options, element) => {
    const { radioButtonGroup, radioButtonValue } = options || {};
    
    let inputElement = null;
    
    if( radioButtonGroup ) {
        inputElement = createInputElement("radio", "layer-visible", element);
        inputElement.name = radioButtonGroup;
        inputElement.value = radioButtonValue;
    } else {
        inputElement = createInputElement("checkbox", "layer-visible", element);
    }
    
    return inputElement;
}


class LayerListHeader {

    constructor( parent ) {
        this.element = createElement("li", "layer-list-header", parent);
        this.titleElement = createElement("span", "layer-list-header-title", this.element);

        this.closeElement = createElement("span", "layer-list-header-buttons", this.element);
        this.closeElement.appendChild(icon( faX ).node[0]);        
        this.setTitle(null);
    }
    
    setTitle(title) {
        if( title ) {
            this.titleElement.innerText = title;         
        } else {
            this.titleElement.innerHTML = '&nbsp;';       
        }
    }
    
}

class LayerListEntry extends AbstractLayerEntry {
	
	constructor( layer, parent, options ) {
		super(layer);
		this.control = parent;
		
		const container = createElement("li", "layer-entry");
		const element = createElement("div", "layer-entry-columns", container);
		
		// tree state control
        new LayerTreeState(layer, element);

        const { radioButtonGroup } = options || {}
        const visible = createLayerVisible(options, element);

        const icon = new LayerIcon(element);
        const label = new LayerLabel(element, () => this.onSelectLayer() );

        const opacity = createInputElement("range", "layer-opacity", element, layer.hasCapability(OPACITY));
        const filter = new IconButton(element, faFilter, "layer-filter", layer.hasCapability(FILTER));
        const featureInfo = new IconButton(element, faInfoCircle, "layer-feature-info", layer.hasCapability(FEATURE_INFO));
        const legend = new IconButton(element, faList, "layer-legend", layer.hasCapability(LEGEND));
        const removeIcon = createRemoveIcon(element, layer.hasCapability(REMOVEABLE));

        createLayerSortGrip(element, layer.hasCapability(SORTABLE));

		this.listen(this.layer.on("propertychange", ({ key }) => {
			this.updateRow(key);
		}));
		
		// in case of a radio button group, the event is handles by the parent
		// as we both need to hide previous layer as show the new layer
		if( !radioButtonGroup ) {
            visible.addEventListener(EventType.CHANGE, this.handleVisibleChange.bind(this), false);
        }

        if (opacity) {
            opacity.min = 0;
            opacity.max = 1;
            opacity.step = 0.01;
            opacity.addEventListener(EventType.CHANGE, this.handleOpacityChange.bind(this), false);
            opacity.addEventListener("input", this.handleOpacityChange.bind(this), false)
        }
        
        if (filter) {
            filter.addEventListener(EventType.CLICK, e => this.onFilterLayer(), false);
        }

        if (featureInfo) {
            featureInfo.addEventListener(EventType.CLICK, this.handleFeatureInfoChange.bind(this), false);
        }
                
        if (legend) {
            legend.addEventListener(EventType.CLICK, this.handleLegendChange.bind(this), false);
        }
        
        if( removeIcon ) {
            removeIcon.addEventListener(EventType.CLICK, e => this.onRemoveRow(), false);
        }

		this.visible = visible;
		this.icon = icon;
		this.label = label;
        this.opacity = opacity;
        this.filter = filter;
        this.featureInfo = featureInfo;
        this.legend = legend;
        
		this.container = container;
		
		container.dataset.olId = layer.getId();
	}
	
	handleVisibleChange(event) {
		this.instance.setVisible(event.target.checked);
	}
	
	handleOpacityChange(event) {
		this.instance.setOpacity(parseFloat(event.target.value));
	}
	
    handleFilterChange(event) {
        this.featureInfo.checked = !this.featureInfo.checked;
        this.instance.setFeatureInfoActive(this.featureInfo.checked);
    }
	
	handleFeatureInfoChange(event) {
        this.featureInfo.checked = !this.featureInfo.checked; 
		this.instance.setFeatureInfoActive( this.featureInfo.checked );
	}
	
	handleLegendChange(event) {
        this.legend.checked = !this.legend.checked;        
		this.instance.setLegendVisible( this.legend.checked );
	}
	
	updateAll() {
		this.updateRow(NAME_KEY);
		this.updateRow(ICON_KEY);
		this.updateRow('visible');
		this.updateRow('opacity');
		this.updateRow(FILTER_ACTIVE_KEY);
		this.updateRow(LEGEND_VISIBLE_KEY);
		this.updateRow(FEATURE_INFO_ACTIVE_KEY);
        this.updateRow(LAYER_ERROR_KEY);
	}
	
	updateRow(key) {	
		if( key === NAME_KEY ) {
			this.label.update( this.instance.isClickable(), this.instance.getName() || "");
		} else if( key === ICON_KEY || key === LAYER_ERROR_KEY ) {
			this.icon.value = getLayerIcon(this.instance);
		} else if( key === 'visible' ) {
			this.visible.checked = this.instance.isVisible();
		} else if( this.opacity && key === 'opacity') {
			this.opacity.value = this.instance.getOpacity();
	    } else if( this.filter && key === FILTER_ACTIVE_KEY ) {
            this.filter.checked = this.instance.isFilterActive();
		} else if( this.legend && key === LEGEND_VISIBLE_KEY ) {
			this.legend.checked = this.instance.isLegendVisible();
			this.legend.disabled = !this.instance.hasLegend(0);
		} else if( this.featureInfo && key === FEATURE_INFO_ACTIVE_KEY ) { 
			this.featureInfo.checked = this.instance.isFeatureInfoActive();
			this.featureInfo.disabled = !this.instance.hasFeatureInfo();
        } else if (key === 'zIndex') {
            // ignored
		} else {
			console.warn("ignore", key)
		}
    }
    
    onSelectLayer() {
        this.control.onSelectLayer( this );
    }
    
    onFilterLayer() {
        this.control.onFilterLayer(this);
    }

    onRemoveRow() {
        this.control.onRemoveLayer( this );
    }

	removeElement() {
		if( this.container && this.container.parentNode ) {
			this.container.parentNode.removeChild( this.container );
			this.container = undefined;
        }
    }

    remove() {
        this.removeElement();
        super.remove();
    }

}

class LayerListControl {
    
   constructor({parent, reverse, container, eventTarget, layerSource, header}) {
       this.parent = parent;
       this.reverse = reverse;
       this.eventTarget = eventTarget;
       this.layerSource = layerSource;
 
       this.element = createElement("ul", "layer-list-layers", container);
       
       if( header ) {
            this.header = new LayerListHeader(this.element);

            this.header.closeElement.addEventListener("click", event => {
                parent.dispatchEvent(CLOSE_EVENT);
            })
       }       
       
       this.layers = [];
      
       sortableLayers(this.element, {
            onDragOrder: this.onHandleSort.bind(this),
            onUpdateOrder: this.onHandleSortComplete.bind(this)
        });
     
    }

    setTitle(title) {
        if( this.header ) {
            this.header.setTitle(title);
        }     
    }
    
    getLayers() {
        return this.layers;
    }
   
   updateColumns() {
       const layers = this.getLayers();
       
       const capabilities = createEmpty(true);
       const keys = Object.keys(capabilities);

        for(let i = 0; i < layers.length; i++) {
            const layer = layers[i];

            for(let j = 0; j < keys.length; j++) {
                const key = keys[j];
                capabilities[key] = capabilities[key] && !layer.hasCapability(key);
            }
        }
        
        for(let i = 0; i < keys.length; i++) {
            const key = keys[i];    
            this.element.classList.toggle( hiddenColumnStyles[key], capabilities[key] );
        }
    }

    addLayer(layer, options) {
        const entry = new LayerListEntry(layer, this.parent, options);

        let beforeElement = null;
        
        if( this.reverse ) {
            beforeElement = this.element.querySelector( ".layer-entry" );
        }    
        
        if( beforeElement != null ) {
            // insert in reverse order - to align with OL order
            this.element.insertBefore(entry.container, beforeElement);
        } else {
            this.element.appendChild(entry.container);
        }

        this.layers.push(layer);

        return entry;
    }
    
    onHandleSort( values ) {
        let zIndexMap = {};
        const layers = this.layerSource();
        
        for(let i = 0; i < values.length; i++) {
            const value = values[i];
            zIndexMap[value.dataset.olId] = i;
        }
               
        for(let i = 0; i < layers.getLength(); i++) {
            const layer = layers.item(i);
            const zIndex = zIndexMap[layer.ol_uid];
            
            if( zIndex !== undefined ) {
                layer.setZIndex(values.length - zIndex);
            }
        }

        //TODO: prevent re-order of layer list index??
    }
    
    onHandleSortComplete( values ) {
        this.onHandleSort(values);
        if( this.eventTarget ) {
            this.eventTarget.dispatchEvent(SORT_LAYER_EVENT);
        } else {
            console.warn("No event target found");
        }
    }
    
    remove() {
        if( this.element ) {
            this.element.remove();
            this.element = undefined;
        } 
    }
}


class Layers extends AbstractLayerControl {
	
	constructor(opt_options) {
        const options = opt_options ? opt_options : {};
        super(options);

    	const className = options.className !== undefined ? options.className : 'ol-layer-list';
		const parentElement = this.element;
		
		parentElement.className = `${className} ${CLASS_UNSELECTABLE} ${CLASS_CONTROL}`;
		
        const containerElement = createElement("div", "layer-list-container", parentElement);
        	
		this.layerListControl = new LayerListControl( {
            parent: this,
            container: containerElement,
            reverse: true,
            eventTarget: this,
            header: true,
            layerSource: () => this.getMap().getLayers()            
        });
   
    }
    
    setTitle(title) {
        this.layerListControl.setTitle(title);
    }

    setMap(map) {
        if( this.sizeHandlerKey ) {
            unByKey( this.sizeHandlerKey );
            this.sizeHandlerKey = null;
        }       
        
        super.setMap(map);
        if( map ) {
            this.layerListControl.updateColumns();
            
            const my = this;
            this.sizeHandlerKey = map.on("change:size", event => {
                my.updateSize();
            })
            
            this.updateSize();            
        }
        
        this.initialised = !!map;
    }
    
    updateSize( ) {
        const [width, height] = this.getMap().getSize();       
        this.element.style.height = "";
        
        const data = this.element.getBoundingClientRect();
        const maxAvailableHeight = height * .95;
      
        if( data.height > maxAvailableHeight ) {
            this.element.style.height = maxAvailableHeight + "px";
        }
    }
	
	addCloseHandler(handler) {
		const key = listen(this, CLOSE_EVENT, event => {
			handler();
        })

		return () => unByKey(key);	
	}
	
    addSortHandler(handler) {
        const key = listen(this, SORT_LAYER_EVENT, event => {
            handler();
        })

        return () => unByKey(key);  
    }

    addRemoveLayerHandler(handler) {
        const key = listen(this, REMOVE_LAYER_EVENT, event => {
            handler(event.layer);
        })      

        return () => unByKey(key);  
    }

    addSelectLayerHandler(handler) {
        const key = listen(this, SELECT_LAYER_EVENT, event => {
            handler(event.layer);
        })      

        return () => unByKey(key);  
    }

    addFilterLayerHandler(handler) {
        const key = listen(this, FILTER_LAYER_EVENT, event => {
            handler(event.layer);
        })      

        return () => unByKey(key);  
    }

    refresh() {
        this.getLayers()
        .forEach(e => e.updateAll());
        
        if( this.initialised ) {
            this.layerListControl.updateColumns();
        }
    }

    acceptLayer(source) {
        if( source ) {
            const { managed } = source.getCapabilities() || {};
            return managed;
        }
    }

    createEntry(layer) {
        return this.layerListControl.addLayer(layer);
    }
    
    onSelectLayer(layerEntry) {
        this.dispatchEvent(new LayerEvent(layerEntry.instance, SELECT_LAYER_EVENT));
    }
    
    onRemoveLayer( layerEntry ) {
        this.dispatchEvent(new LayerEvent(layerEntry.instance, REMOVE_LAYER_EVENT));
    }

    onFilterLayer(layerEntry) {
        this.dispatchEvent(new LayerEvent(layerEntry.instance, FILTER_LAYER_EVENT));
    }
    
}

class LayerEvent extends BaseEvent {
 
    constructor(layer, eventType) {
        super(eventType)
        this.layer = layer;
    }
    
}

export {
	Layers
}